import { useMutation } from '@apollo/client'
import { useToast } from '@ur/react-components'
import { useForm } from '@ur/react-hooks'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import FullLoader from 'components/Loader/FullLoader'
import { Text } from 'components/Text'
import React from 'react'
import styled from 'styled-components'
import {
  ChangePasswordMutation,
  ChangePasswordMutationVariables,
  UserNode,
} from 'types/graphql/users'
import { passwordValidation } from 'util/forms'
import useOnErrorAuto from 'util/hooks/error-handling'
import { CHANGE_PASSWORD_MUTATION } from '../mutations'

const Wrapper = styled.div`
  display: flex;
  height: 100vh;
  flex-direction: column;
  width: 100%;
  padding: 0 1em 40px 1em;
`
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
interface ChangePasswordProps {
  user: UserNode
  area?: string
}

export const ChangePassword: React.UFC<ChangePasswordProps> = ({ user }) => {
  const onErrorAuto = useOnErrorAuto()
  const toasts = useToast()

  const validatePassword = (value: string) =>
    passwordValidation(value) ? null : 'For svakt passord'
  const validatePasswordAgain = (value: string, other: string) =>
    value === other ? null : 'Passordene er ulike'

  const {
    formValues: form,
    formValid,
    formErrors: errors,
    formChangeHandler: handler,
    resetForm,
    submitHandler,
  } = useForm({
    values: {
      currentPassword: '',
      newPassword: '',
      newPasswordAgain: '',
    },
    validators: {
      currentPassword: val => (!!val ? null : 'Påkrevd'),
      newPassword: validatePassword,
      newPasswordAgain: (value, { newPassword }) =>
        validatePasswordAgain(value, newPassword),
    },
    config: {
      initAsInvalid: true,
    },
  })

  const [changePassword, { loading: changePasswordLoading }] = useMutation<
    ChangePasswordMutation,
    ChangePasswordMutationVariables
  >(CHANGE_PASSWORD_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Me'],
    onCompleted(data) {
      if (data?.changePassword.ok) {
        toasts('success', 'Passordet ditt ble endret ')
      } else toasts('error', 'Kunne ikke endre passordet')
    },
    onError: onErrorAuto('Kunne ikke endre passordet'),
  })

  async function submitPassword(values: typeof form) {
    try {
      await changePassword({
        variables: {
          password: values.currentPassword,
          newPassword: values.newPassword,
        },
      })
      resetForm()
    } catch {}
  }

  return (
    <Wrapper>
      <h3>
        <Text color="primary">Endre passord</Text>
      </h3>
      <FormWrapper>
        <Input
          type="password"
          label="Nåværende passord"
          value={form.currentPassword}
          error={errors.currentPassword}
          fullWidth
          onChange={handler('currentPassword')}
        />

        <Input
          type="password"
          label="Nytt passord"
          value={form.newPassword}
          error={form.newPassword === '' ? null : errors.newPassword}
          fullWidth
          onChange={handler('newPassword', {
            includeValidation: ['newPasswordAgain'],
          })}
        />

        <Input
          type="password"
          label="Nytt passord igjen"
          value={form.newPasswordAgain}
          error={form.newPasswordAgain === '' ? null : errors.newPasswordAgain}
          fullWidth
          onChange={handler('newPasswordAgain', {
            includeValidation: ['newPassword'],
          })}
        />

        {changePasswordLoading ? (
          <FullLoader />
        ) : (
          <Button
            type="submit"
            disabled={!formValid}
            onClick={submitHandler(submitPassword)}
          >
            Oppdater passord
          </Button>
        )}
      </FormWrapper>
    </Wrapper>
  )
}
